import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { uploadOne } from "../../hooks/useS3Upload";
import {
  DEFAULT_HOLD_MUSIC_PRESET,
  HOLD_MUSIC_ACCEPT,
  HOLD_MUSIC_PRESET_STREAMS,
  holdMusicFileErrorMessage,
  holdMusicValueFromSelection,
  parseHoldMusicSelection,
  validateHoldMusicFile,
} from "../../utils/voiceHoldMusic";

const PRESETS = [
  { id: "calm", titleKey: "holdMusicCalm", title: "Suave", hintKey: "holdMusicCalmHint", hint: "Calma, para espera longa." },
  { id: "classic", titleKey: "holdMusicClassic", title: "Clássica", hintKey: "holdMusicClassicHint", hint: "Orquestra leve." },
  { id: "lively", titleKey: "holdMusicLively", title: "Animada", hintKey: "holdMusicLivelyHint", hint: "Ritmo mais presente." },
];

export default function VoiceHoldMusicPicker({ t, value, onChange }) {
  const inputRef = useRef(null);
  const parsed = useMemo(() => parseHoldMusicSelection(value), [value]);
  const [wantsCustom, setWantsCustom] = useState(parsed.kind === "custom");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [previewError, setPreviewError] = useState(false);
  const [fileName, setFileName] = useState("");

  useEffect(() => {
    if (parsed.kind === "custom") setWantsCustom(true);
  }, [parsed.kind]);

  const radioValue =
    wantsCustom || parsed.kind === "custom"
      ? "custom"
      : parsed.id || DEFAULT_HOLD_MUSIC_PRESET;
  const previewSrc =
    parsed.kind === "custom" && parsed.url
      ? parsed.url
      : HOLD_MUSIC_PRESET_STREAMS[radioValue] || HOLD_MUSIC_PRESET_STREAMS.calm;

  useEffect(() => {
    setPreviewError(false);
  }, [previewSrc]);

  const label = (key, fallback) =>
    t(`channelForm.twilioPhone.${key}`, fallback);

  const setPreset = (id) => {
    setError(null);
    setPreviewError(false);
    setWantsCustom(false);
    onChange(holdMusicValueFromSelection("preset", { presetId: id }));
  };

  const onRadio = (next) => {
    if (next === "custom") {
      setWantsCustom(true);
      if (parsed.kind === "custom" && parsed.url) return;
      inputRef.current?.click();
      return;
    }
    setPreset(next);
  };

  const onPick = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError(null);
    const check = await validateHoldMusicFile(file);
    if (!check.ok) {
      setError(holdMusicFileErrorMessage(check.reason, t));
      return;
    }
    setBusy(true);
    try {
      const uploaded = await uploadOne(file, { prefix: "voice-hold-music" });
      const url = String(uploaded?.url || "").trim();
      if (!/^https:\/\//i.test(url)) {
        throw new Error("no_url");
      }
      setFileName(file.name);
      onChange(url);
    } catch (err) {
      setError(
        t(
          "channelForm.twilioPhone.holdMusicErrUpload",
          "Não deu para enviar o áudio. Tente de novo em MP3 ou WAV."
        )
      );
      console.warn("[VoiceHoldMusicPicker] upload:", err?.message || err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <FormControl component="fieldset" fullWidth>
      <Typography variant="subtitle2" fontWeight={700}>
        {label("holdMusicTitle", "Música enquanto o cliente espera")}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.25 }}>
        {label(
          "holdMusicHelper",
          "Escolha um estilo pronto ou envie um MP3/WAV curto (até 5 MB), sem voz falando por cima."
        )}
      </Typography>

      <RadioGroup
        value={radioValue}
        onChange={(e) => onRadio(e.target.value)}
      >
        {PRESETS.map((p) => (
          <FormControlLabel
            key={p.id}
            value={p.id}
            control={<Radio size="small" />}
            sx={{ alignItems: "flex-start", mb: 0.25 }}
            label={
              <Box sx={{ pt: 0.25 }}>
                <Typography variant="body2" fontWeight={600}>
                  {label(p.titleKey, p.title)}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {label(p.hintKey, p.hint)}
                </Typography>
              </Box>
            }
          />
        ))}
        <FormControlLabel
          value="custom"
          control={<Radio size="small" />}
          sx={{ alignItems: "flex-start" }}
          label={
            <Box sx={{ pt: 0.25 }}>
              <Typography variant="body2" fontWeight={600}>
                {label("holdMusicCustom", "Meu áudio")}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {label("holdMusicCustomHint", "Só MP3 ou WAV. Sem M4A, OGG ou vídeo.")}
              </Typography>
            </Box>
          }
        />
      </RadioGroup>

      <input
        ref={inputRef}
        type="file"
        hidden
        accept={HOLD_MUSIC_ACCEPT}
        onChange={onPick}
      />

      {radioValue === "custom" ? (
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
          >
            {busy
              ? label("holdMusicUploading", "Enviando…")
              : parsed.url
                ? label("holdMusicReplace", "Trocar arquivo")
                : label("holdMusicPick", "Escolher arquivo")}
          </Button>
          {busy ? <CircularProgress size={16} /> : null}
          {parsed.url ? (
            <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 280 }}>
              {fileName || label("holdMusicCustomReady", "Áudio enviado. Salve o canal para usar.")}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {label("holdMusicCustomEmpty", "Ainda sem arquivo — a espera usa a música suave.")}
            </Typography>
          )}
        </Stack>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mt: 1.25 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}

      {(radioValue !== "custom" && previewSrc) || parsed.url ? (
        <Box sx={{ mt: 1.5 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
            {label("holdMusicPreview", "Ouvir um trecho")}
          </Typography>
          <audio
            key={previewSrc}
            controls
            preload="metadata"
            controlsList="nodownload noplaybackrate"
            src={previewSrc}
            onError={() => setPreviewError(true)}
            onLoadedMetadata={() => setPreviewError(false)}
            style={{ width: "100%", maxWidth: 420, height: 36 }}
          />
          {previewError ? (
            <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
              {label(
                "holdMusicPreviewErr",
                "Não deu para tocar aqui. A ligação ainda usa este estilo — tente de novo ou outro estilo."
              )}
            </Typography>
          ) : null}
        </Box>
      ) : null}
    </FormControl>
  );
}
